/** Plugin config `name` — must match PluginMetadata id in plugin-metadata.ts. */
export const AUSTRALIAN_SUPER_PLUGIN_NAME = "super";

/** Plugin config `id` — must match the id assigned by the backend plugin registry. */
export const AUSTRALIAN_SUPER_PLUGIN_ID = "6";

/** Live GraphQL endpoint (UAT, PROD, and non-local dev builds). */
export const AUSTRALIAN_SUPER_DEPLOYED_GRAPHQL_ORIGIN =
  "https://new.plugin.gloss-vault.com/australian-super";

const AUSTRALIAN_SUPER_LOCAL_DEV_GRAPHQL_URL = "http://localhost:4000";

/** True only for `npm run watch` / `gloss:build:local` (IS_LOCAL=true). */
export function isAustralianSuperLocalDev(): boolean {
  return typeof process !== "undefined" && process.env?.IS_LOCAL === "true";
}

/**
 * Default URL: deployed origin for UAT/PROD and all CI builds; localhost only when IS_LOCAL.
 * At fetch time, {@link resolvePluginConfigForFetch} applies vault URL with the same live guard.
 */
export const AUSTRALIAN_SUPER_DEFAULT_GRAPHQL_URL = isAustralianSuperLocalDev()
  ? AUSTRALIAN_SUPER_LOCAL_DEV_GRAPHQL_URL
  : AUSTRALIAN_SUPER_DEPLOYED_GRAPHQL_ORIGIN;

/** Canonical URL for Australian Super GraphQL — use for saves and non-local fetches. */
export function effectiveAustralianSuperGraphqlUrl(vaultUrl?: string): string {
  const trimmed = vaultUrl?.trim();
  if (!trimmed) {
    return AUSTRALIAN_SUPER_DEFAULT_GRAPHQL_URL;
  }
  if (!isAustralianSuperLocalDev() && isLocalPluginGraphqlUrl(trimmed)) {
    return AUSTRALIAN_SUPER_DEPLOYED_GRAPHQL_ORIGIN;
  }
  return trimmed;
}

function isLocalPluginGraphqlUrl(url: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(url);
}

export type AustralianSuperPluginField = {
  field: string;
  label: string;
  type: "text" | "password";
  required: boolean;
  defaultValue: string;
};

export type AustralianSuperPluginConfig = {
  id: string;
  name: string;
  url: string;
  fields: AustralianSuperPluginField[];
};

/** Default Australian Super plugin registration (C1 wire-up). */
export const australianSuperPluginConfig: AustralianSuperPluginConfig = {
  id: AUSTRALIAN_SUPER_PLUGIN_ID,
  name: AUSTRALIAN_SUPER_PLUGIN_NAME,
  url: AUSTRALIAN_SUPER_DEFAULT_GRAPHQL_URL,
  fields: [
    {
      field: "id",
      label: "Member number",
      type: "text",
      required: true,
      defaultValue: "",
    },
    {
      field: "pin",
      label: "PIN",
      type: "password",
      required: true,
      defaultValue: "",
    },
  ],
};

/**
 * Build GraphQL `auth(payload: { … })` inline fields from caller inputs.
 * Strings are JSON-quoted; booleans/numbers are emitted as GraphQL literals (e.g. `send: true`).
 */
export function buildGraphQLAuthPayloadLiteral(args: Record<string, unknown>): string {
  return Object.keys(args)
    .map((key) => {
      const value = args[key];
      if (typeof value === "string") {
        return `${key}: ${JSON.stringify(value)}`;
      }
      if (typeof value === "boolean" || typeof value === "number") {
        return `${key}: ${value}`;
      }
      throw new Error(`Unsupported GraphQL literal type for key "${key}": ${typeof value}`);
    })
    .join(", ");
}

export function isAustralianSuperPluginConfig(config: {
  name: string;
  fields?: { field: string }[];
}): config is AustralianSuperPluginConfig {
  return (
    config.name === AUSTRALIAN_SUPER_PLUGIN_NAME &&
    Array.isArray(config.fields) &&
    config.fields.some((f) => f.field === "id") &&
    config.fields.some((f) => f.field === "pin")
  );
}

export function getAustralianSuperPluginConfig(
  configs: { name: string; id?: string; url?: string; fields?: { field: string }[] }[],
): AustralianSuperPluginConfig | undefined {
  const found = configs.find((c) => c.name === AUSTRALIAN_SUPER_PLUGIN_NAME);
  if (!found || !isAustralianSuperPluginConfig(found)) {
    return undefined;
  }
  return found;
}
